import { useQuery } from "@apollo/client";
import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@material-ui/core";
import { Close, FilterList } from "@material-ui/icons";
import { DocumentNode } from "graphql";
import { Dispatch, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GET_FILTERED_AGREEMENT_COUNT } from "../../containers/agreements/queries";
import { ocean } from "../../hooks/use-map-theme-styles";
import { isEmptyValue } from "../../utils/is-empty-value";
import { sumBy } from "../../utils/sum-by";
import {
	FilterGroup,
	FilterListStateType,
	FilterStateType,
	getSelectedValueCount,
	getWhereExpression,
	TableFilterAction,
} from "./models";
import TabsGroup from "./TabsGroup";
import { renderFilterComponent } from "./renderFilterComponent";
import useStyles from "./styles";

function PrecalculatedCounter(props: { countQuery: DocumentNode; module?: string; state: FilterListStateType; title: string }) {
	const { t } = useTranslation();
	const whereExpression = getWhereExpression(props.state);
	const { data } = useQuery(props.countQuery, {
		variables: {
			where: whereExpression,
		},
		skip:
			props.countQuery === GET_FILTERED_AGREEMENT_COUNT &&
			whereExpression?.AND?.some((element: any) =>
				["timePeriod_GTE", "timePeriod_LTE", "invoiceTimePeriod_GTE", "invoiceTimePeriod_LTE"].includes(
					Object.keys(element)[0] as any
				)
			),
		context: { module: props.module },
	});
	const filteredRecords =
		data && [(Object.values(data)[0] as any)?.[0]?._count, (Object.values(data)[0] as any)?._count].find((x) => !isEmptyValue(x));

	return !isEmptyValue(filteredRecords) ? (
		<Typography color="textSecondary" align="left" style={{ marginRight: "10px" }}>
			{filteredRecords} {t(props.title)} {t("filtered-entity")}
		</Typography>
	) : null;
}

interface IProps {
	state: FilterListStateType;
	isDialogOpen: boolean;
	dispatcher: Dispatch<TableFilterAction>;
	title: string;
	countQuery?: DocumentNode;
	count?: number;
	module?: string;
	setOpenDialog: (isOpen: boolean) => void;
	handleApply: () => void;
	disabled?: boolean;
	groups?: FilterGroup[];
}

export function FilterDialog(props: IProps) {
	const { t } = useTranslation();
	const { buttonFilter, dialogTitle, dialogScrollable, primaryText, disabledText } = useStyles();
	const [stateBackup, setStateBackup] = useState(props.state);
	const [isCleared, setIsCleared] = useState(false);

	function renderSelectedValueCountByFilter(state: FilterStateType) {
		const count = getSelectedValueCount(state);
		return count ? `(${count})` : "";
	}

	function handleApply() {
		setStateBackup(props.state);
		props.handleApply();
		props.setOpenDialog(false);
	}

	function handleClose() {
		props.dispatcher({ type: "restore-state", state: stateBackup });
		props.setOpenDialog(false);
	}

	function handleClearAll() {
		props.dispatcher({ type: "clear-all" });
		setIsCleared(true);
	}

	useEffect(() => {
		if (isCleared) {
			setStateBackup(props.state);
			props.handleApply();
		}
		setIsCleared(false);
	}, [props.state]);

	useEffect(() => {
		if (!props.isDialogOpen) {
			setStateBackup(props.state);
		}
	}, [props.state, props.isDialogOpen]);

	const selectedValueCount = sumBy(props.state.map(getSelectedValueCount), (x) => x);
	const groupedFilterFields = new Set(
		(props.groups || []).flatMap((group) => group.filters.map((filter) => String(filter.field)))
	);
	const ungroupedFilters = props.state.filter((filter) => !groupedFilterFields.has(String(filter.field)));
	const groups = props.groups || [];

	return (
		<Box flexShrink={0}>
			<Button
				disabled={props.disabled}
				startIcon={
					<Badge badgeContent={selectedValueCount} color={props.disabled === true ? "secondary" : "primary"}>
						<FilterList color={props.disabled === true ? "disabled" : "primary"} />
					</Badge>
				}
				classes={{ root: buttonFilter }}
				onClick={() => props.setOpenDialog(true)}
				id="tableFilterButton"
			>
				<Typography className={props.disabled === true ? disabledText : primaryText}>{t("all-filters")}</Typography>
			</Button>
			<Dialog maxWidth="xl" aria-labelledby="filter-dialog-title" open={props.isDialogOpen} onClose={handleClose}>
				<DialogTitle classes={{ root: dialogTitle }} id="filter-dialog-title">
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography>
							{t("filters").toUpperCase()} {selectedValueCount ? `(${selectedValueCount})` : ""}
						</Typography>
						<IconButton onClick={handleClose}>
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent dividers classes={{ root: dialogScrollable }}>
					<Box width="60vw" className="scrollable">
						{groups.map((group) => {
							const groupSelectedValueCount = sumBy(group.filters.map(getSelectedValueCount), (x) => x);
							const labelKey = group.labelKey || group.id;
							
							return (
								<Box key={group.id} id={`filter-group-${group.id}`}>
									<Box display="flex" width="100%" pl={1} pb={1} alignItems="center" justifyContent="space-between">
										<Box display="flex" justifyContent="flex-start" alignItems="center">
											<Typography style={{ color: ocean, fontSize: "14px" }} align="left">
												{t(labelKey).toUpperCase()} {groupSelectedValueCount ? `(${groupSelectedValueCount})` : ""}
											</Typography>
										</Box>
										<Divider variant="middle" style={{ flexGrow: 1, backgroundColor: ocean }} />
									</Box>
									<Box display="flex">
										<Fragment>
											<TabsGroup
												tabs={group.filters.map((filter) => ({ filter }))}
												usePopover={false}
												dispatcher={props.dispatcher}
												exclusive={group.exclusive}
											/>
										</Fragment>
									</Box>
								</Box>
							);
						})}
						{ungroupedFilters.map((filter, index) => (
							<Box key={`${String(filter.field)}-${index}`} id={`filter-${String(filter.field)}`}>
								<Box display="flex" width="100%" pl={1} pb={1} alignItems="center" justifyContent="space-between">
									<Box display="flex" justifyContent="flex-start" alignItems="center">
										<Typography style={{ color: ocean, fontSize: "14px" }} align="left">
											{t(filter.localizationKey || (filter.field as string)).toUpperCase()}{" "}
											{renderSelectedValueCountByFilter(filter)}
										</Typography>
									</Box>
									<Divider variant="middle" style={{ flexGrow: 1, backgroundColor: ocean }} />
								</Box>
								<Box display="flex">
									<Fragment key={`${String(filter.field)}-${index}`}>
										{renderFilterComponent(filter, { usePopover: false, dispatcher: props.dispatcher })}
									</Fragment>
								</Box>
							</Box>
						))}
					</Box>
				</DialogContent>
				<DialogActions>
					<Box width="100%" margin={2} display="flex" alignItems="center" justifyContent="space-between">
						<Button variant="outlined" color="primary" onClick={handleClearAll}>
							{t("clear-all-filters")}
						</Button>
						<Box display="flex" alignItems="center">
							{props.countQuery && (
								<PrecalculatedCounter
									countQuery={props.countQuery}
									module={props.module}
									state={props.state}
									title={props.title}
								/>
							)}
							{props.count !== undefined && (
								<Typography color="textSecondary" align="left" style={{ marginRight: "10px" }}>
									{props.count} {t(props.title)} {t("filtered-entity")}
								</Typography>
							)}
							<Button variant="contained" color="primary" onClick={handleApply}>
								{t("show-filtered-results")}
							</Button>
						</Box>
					</Box>
				</DialogActions>
			</Dialog>
		</Box>
	);
}